import React, { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import {
  fetchCustomerSettings, saveBillingCutoff, saveShowProductHints, saveSensorAlert,
  CustomerSettings,
} from '../services/dataService';

interface Props {
  secretId: string;
  onClose: () => void;
  onSaved: () => void;
}

// The preset ladder Martin specified, plus a free field. The 1000 floor is
// enforced in the database too — a threshold below that would fire constantly
// on an ordinary household and train people to ignore the mail.
const PRESETS = [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000];

const SettingsModal: React.FC<Props> = ({ secretId, onClose, onSaved }) => {
  const [settings, setSettings] = useState<CustomerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(false);
  // "Custom" has to be its own state. Deriving it from the value alone meant
  // that picking it while the value happened to be a preset put the select
  // straight back on that preset — the option was unreachable.
  const [customMode, setCustomMode] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCustomerSettings(secretId).then(s => { setSettings(s); setLoading(false); });
  }, [secretId]);

  const patchAlert = (sensorId: string, patch: Partial<CustomerSettings['alerts'][0]>) => {
    setSettings(s => s && ({
      ...s,
      alerts: s.alerts.map(a => a.sensorId === sensorId ? { ...a, ...patch } : a),
    }));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);

    const errs: string[] = [];
    const e1 = await saveBillingCutoff(secretId, settings.billingCutoffDay);
    if (e1) errs.push(e1);
    const e2 = await saveShowProductHints(secretId, settings.showProductHints);
    if (e2) errs.push(e2);
    for (const a of settings.alerts) {
      const e = await saveSensorAlert(secretId, a.sensorId, a.enabled, a.liters, a.email);
      if (e) errs.push(`${a.alias ?? a.deviceId}: ${e}`);
    }

    setSaving(false);
    if (errs.length) { setError(errs[0]); return; }
    setSavedAt(true);
    onSaved();
    setTimeout(() => setSavedAt(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
          <h2 className="font-semibold text-slate-100">Configuración</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : !settings ? (
          <p className="p-5 text-sm text-red-400">No se pudo cargar la configuración.</p>
        ) : (
          <>
            <div className="overflow-y-auto flex-1 p-5 space-y-6">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Día de corte del recibo</label>
                <input
                  type="number" min={1} max={31}
                  value={settings.billingCutoffDay ?? ''}
                  onChange={e => setSettings({
                    ...settings,
                    billingCutoffDay: e.target.value === '' ? null : Number(e.target.value),
                  })}
                  placeholder="sin definir"
                  className="w-28 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:border-cyan-500 outline-none"
                />
                <p className="text-xs text-slate-500 mt-1">
                  El día del mes en que cierra tu recibo. Se usa para proyectar el consumo del
                  periodo; sin él no se muestra el pronóstico.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={settings.showProductHints}
                    onChange={e => setSettings({ ...settings, showProductHints: e.target.checked })}
                  />
                  Mostrar sugerencias de otros productos M3TR
                </label>
              </div>

              <div>
                <h3 className="text-sm text-slate-300 mb-1">Alertas por consumo</h3>
                <p className="text-xs text-slate-500 mb-3">
                  Te avisamos por correo cada vez que un sensor acumule la cantidad que elijas.
                  Están apagadas hasta que las enciendas.
                </p>

                {settings.alerts.length === 0 && (
                  <p className="text-xs text-slate-500">No hay sensores configurables.</p>
                )}

                <div className="space-y-4">
                  {settings.alerts.map(a => (
                    <div key={a.sensorId} className="border border-slate-700 rounded-lg p-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-slate-200">
                        <input
                          type="checkbox"
                          checked={a.enabled}
                          onChange={e => patchAlert(a.sensorId, { enabled: e.target.checked })}
                        />
                        {a.alias ?? a.deviceId}
                      </label>

                      {a.enabled && (
                        <div className="space-y-2 pl-6">
                          <div className="flex items-center gap-2">
                            <select
                              value={customMode.has(a.sensorId) || !PRESETS.includes(a.liters ?? 0) ? 'custom' : String(a.liters)}
                              onChange={e => {
                                const next = new Set(customMode);
                                if (e.target.value === 'custom') {
                                  next.add(a.sensorId);
                                  patchAlert(a.sensorId, { liters: a.liters ?? 1000 });
                                } else {
                                  next.delete(a.sensorId);
                                  patchAlert(a.sensorId, { liters: Number(e.target.value) });
                                }
                                setCustomMode(next);
                              }}
                              className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100"
                            >
                              {PRESETS.map(p => <option key={p} value={p}>cada {p.toLocaleString()} L</option>)}
                              <option value="custom">otra cantidad…</option>
                            </select>
                            {(customMode.has(a.sensorId) || !PRESETS.includes(a.liters ?? 0)) && (
                              <input
                                type="number" min={1000} step={100}
                                value={a.liters ?? 1000}
                                onChange={e => patchAlert(a.sensorId, { liters: Number(e.target.value) })}
                                className="w-28 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100"
                              />
                            )}
                            {(a.liters ?? 0) < 1000 && (
                              <span className="text-xs text-amber-500">mínimo 1000 L</span>
                            )}
                          </div>
                          <input
                            type="email"
                            value={a.email ?? ''}
                            onChange={e => patchAlert(a.sensorId, { email: e.target.value })}
                            placeholder="correo para el aviso"
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100"
                          />
                          <p className="text-xs text-slate-500">
                            {a.lastSentAt
                              ? `Último email enviado: ${new Date(a.lastSentAt).toLocaleString()}`
                              : 'Último email enviado: ninguno todavía'}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}
            </div>

            <div className="px-5 py-4 border-t border-slate-700 flex justify-end gap-2 shrink-0">
              <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200">Cerrar</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white rounded-lg font-medium flex items-center gap-1.5"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : savedAt ? <Check className="w-4 h-4" /> : null}
                {saving ? 'Guardando…' : savedAt ? 'Guardado' : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsModal;
